import React from 'react';
import { Minus, Plus, Target } from 'lucide-react';
import {
  getCreateUnitCompatibilityFromTreeSelection,
  getLeafTopics
} from '../utils/topicUtils';
import { extractTopicMetadata } from '../utils/curriculumUtils';
import { maxTopicsPerUnit } from '../constants';
import {
  planCreateUnitCompactButtonClass,
  planDestructiveCompactButtonClass,
} from '../planFloatingActionsTheme';

interface CreateUnitTreeButtonProps {
  onOpenCreateUnitWizard: () => void;
  selectedTopics?: any[];
  studentCurriculumData?: any;
  onCreateAssessment?: () => void;
  onRemoveTopics?: () => void;
  className?: string;
}

/**
 * Compact Create Units tree-column actions.
 * Create Unit is enabled when nothing is selected, or when the selection fits single-subject or blended rules.
 * Create Assessment is available only within one curriculum placement.
 * Remove Topics is available for every non-empty selection.
 */
export function CreateUnitTreeButton({
  onOpenCreateUnitWizard,
  selectedTopics = [],
  studentCurriculumData,
  onCreateAssessment,
  onRemoveTopics,
  className = '',
}: CreateUnitTreeButtonProps) {
  const hasSelectedTopics = selectedTopics.length > 0;
  const leafTopicsCount = getLeafTopics(selectedTopics).length;
  const createUnitCompatibility = getCreateUnitCompatibilityFromTreeSelection(
    studentCurriculumData,
    selectedTopics,
    extractTopicMetadata,
    maxTopicsPerUnit
  );
  const canCreateUnit = createUnitCompatibility.canCreateUnit;
  const canCreateAssessment = (() => {
    if (!hasSelectedTopics) return false;

    const firstMetadata = extractTopicMetadata(selectedTopics[0], studentCurriculumData);
    const subjectId = firstMetadata.subjectId?.trim();
    const keyStageId = firstMetadata.keyStageId?.trim();
    const yearId = firstMetadata.yearId?.trim();
    if (!subjectId || !keyStageId || !yearId) return false;

    return selectedTopics.every((topic) => {
      const metadata = extractTopicMetadata(topic, studentCurriculumData);
      return (
        metadata.subjectId?.trim() === subjectId &&
        metadata.keyStageId?.trim() === keyStageId &&
        metadata.yearId?.trim() === yearId
      );
    });
  })();

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <button
        type="button"
        className={`${planCreateUnitCompactButtonClass} disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-action-blue`}
        onClick={onOpenCreateUnitWizard}
        disabled={!canCreateUnit}
        title={canCreateUnit ? undefined : createUnitCompatibility.reason}
      >
        <Plus size={14} />
        <span>Create Unit</span>
      </button>
      {canCreateAssessment && onCreateAssessment && (
        <button
          type="button"
          className={planCreateUnitCompactButtonClass}
          onClick={onCreateAssessment}
        >
          <Target size={14} />
          <span>Create Assessment</span>
        </button>
      )}
      {hasSelectedTopics && onRemoveTopics && (
        <button
          type="button"
          className={planDestructiveCompactButtonClass}
          onClick={onRemoveTopics}
        >
          <Minus size={14} />
          <span>Remove Topics ({leafTopicsCount})</span>
        </button>
      )}
    </div>
  );
}
